import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Eye, EyeOff } from "lucide-react";

const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";

export default function PasswordEdit() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Le token de réinitialisation arrive dans l'URL (?reset_password_token=XXX)
  const resetToken = searchParams.get("reset_password_token") ?? "";

  const validate = () => {
    const next: Record<string, string> = {};
    if (!password) next.password = "Le mot de passe est requis";
    else if (password.length < 6) next.password = "Au moins 6 caractères";
    if (!passwordConfirmation) next.passwordConfirmation = "La confirmation est requise";
    else if (password !== passwordConfirmation) next.passwordConfirmation = "Les mots de passe ne correspondent pas";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    if (!validate()) {
      e.preventDefault();
      return;
    }
    setSubmitting(true);
    // Le formulaire se soumet nativement (PUT /users/password → redirection HTTP)
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md mx-4 bg-background rounded-2xl shadow-2xl border border-border p-8 animate-in zoom-in-95 duration-200">
        {/* Croix → accueil */}
        <button
          onClick={() => navigate("/")}
          className="absolute right-4 top-4 rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
          aria-label="Retour à l'accueil"
        >
          <X className="h-5 w-5" />
        </button>

        {/* En-tête */}
        <div className="text-center space-y-2 mb-6">
          <h1 className="text-2xl font-display bg-gradient-to-r from-[#f7bbac] to-[#f9bb72] bg-clip-text text-transparent">
            Nouveau mot de passe
          </h1>
          <p className="text-sm text-muted-foreground">
            Choisissez un nouveau mot de passe pour votre compte
          </p>
        </div>

        {/* Formulaire — PUT natif pour redirection HTTP (Rails utilise _method=put) */}
        <form method="POST" action="/users/password" onSubmit={handleSubmit} className="space-y-4">
          <input type="hidden" name="authenticity_token" value={csrf} />
          <input type="hidden" name="_method" value="put" />
          <input type="hidden" name="user[reset_password_token]" value={resetToken} />

          <div className="space-y-2">
            <Label htmlFor="password">Nouveau mot de passe</Label>
            <div className="relative">
              <Input
                id="password"
                name="user[password]"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors({ ...errors, password: "" });
                }}
                className={errors.password ? "border-destructive pr-10" : "pr-10"}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="passwordConfirmation">Confirmer le mot de passe</Label>
            <Input
              id="passwordConfirmation"
              name="user[password_confirmation]"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={passwordConfirmation}
              onChange={(e) => {
                setPasswordConfirmation(e.target.value);
                if (errors.passwordConfirmation) setErrors({ ...errors, passwordConfirmation: "" });
              }}
              className={errors.passwordConfirmation ? "border-destructive" : ""}
            />
            {errors.passwordConfirmation && (
              <p className="text-xs text-destructive">{errors.passwordConfirmation}</p>
            )}
          </div>

          <Button
            type="submit"
            variant="rainbow"
            size="lg"
            className="w-full font-medium"
            disabled={submitting}
          >
            {submitting ? "Enregistrement..." : "Changer le mot de passe"}
          </Button>
        </form>
      </div>
    </div>
  );
}
